/**
 * @file DraggableDataTable.tsx
 *
 * Reusable Gravity data table with optional row drag handles, row actions, and
 * pointer-driven reordering. Wraps Gravity's {@link DataTable}, deriving the
 * responsive `gv-col-*` utilities from the column config and delegating the drag
 * mechanics to {@link usePointerReorderList}. Ported from the design-reviewed
 * Website Builder v2 implementation.
 */

import type { ReactNode } from 'react';
import { DataTable, Icon } from '@group.one/react-gravity';
import type { IconName } from '@group.one/gravity';
import { useMediaQuery } from '../hooks/use-media-query';
import { usePointerReorderList } from '../hooks/use-pointer-reorder-list';
import { ReorderPreview } from './ReorderPreview';
import styles from './DraggableDataTable.module.css';

/** Gravity ships `gv-col-1` … `gv-col-12` data-table utilities. */
const MAX_GRAVITY_DATA_TABLE_COLUMNS = 12;

/** Configuration for a single custom-rendered column. */
export interface DraggableDataTableColumnConfig<T extends object> {
  label: ReactNode;
  className?: string;
  shrink?: boolean;
  renderCell?: (row: T, index: number) => ReactNode;
}

/** A column is keyed by one of `T`'s string keys; its value is a label or a config. */
export type DraggableDataTableColumn<T extends object> = {
  [Key in Extract<keyof T, string>]: {
    [ColumnKey in Key]: string | DraggableDataTableColumnConfig<T>;
  };
}[Extract<keyof T, string>];

/** A row action rendered as an icon button on the right of each row. */
export interface DraggableDataTableAction<T> {
  icon?: IconName;
  label?: string;
  /** Optional `data-testid` for QA automation. */
  testId?: string;
  element?: ReactNode;
  shouldRenderAction?: (row: T, index: number) => boolean;
  disabled?: boolean | ((row: T, index: number) => boolean);
  requestConfirmation?: (row: T, index: number, confirmAction: () => void) => void;
  onAction?: (row: T, index: number) => void;
}

export interface DraggableDataTableProps<T extends { id: string }> {
  headerColumns: Array<DraggableDataTableColumn<T>>;
  hiddenColumns?: Array<Extract<keyof T, string>>;
  rows: T[];
  showHandle?: boolean;
  hideHandleOnMobile?: boolean;
  handleIcon?: IconName;
  dragHandleAriaLabel?: string;
  disabled?: boolean;
  actions?: Array<DraggableDataTableAction<T>>;
  onReorder: (nextRows: T[]) => void;
  getPreviewLabel?: (row: T, index: number) => ReactNode;
  /** Namespaces the per-row `data-testid` and the drag-handle `data-testid`. */
  testIdPrefix?: string;
}

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

function isColumnConfig<T extends object>(
  value: string | DraggableDataTableColumnConfig<T>,
): value is DraggableDataTableColumnConfig<T> {
  return typeof value === 'object' && value !== null;
}

function resolveDisabled<T extends object>(
  disabled: DraggableDataTableAction<T>['disabled'],
  row: T,
  index: number,
): boolean {
  return typeof disabled === 'function' ? disabled(row, index) : Boolean(disabled);
}

/**
 * Renders a reorderable Gravity data table.
 *
 * @param props - The column config, rows, row actions, and the `onReorder`
 *   callback that receives the next ordered array after a drag.
 *
 * @returns The table element (plus the floating drag preview while dragging).
 */
export function DraggableDataTable<T extends { id: string }>(props: DraggableDataTableProps<T>) {
  const {
    headerColumns,
    hiddenColumns,
    rows,
    onReorder,
    showHandle = true,
    hideHandleOnMobile = true,
    handleIcon = 'drag_indicator',
    dragHandleAriaLabel = 'Drag to reorder row',
    disabled = false,
    getPreviewLabel,
    actions,
    testIdPrefix,
  } = props;

  const isMobile = useMediaQuery('Mobile');
  const reorder = usePointerReorderList(rows, onReorder);
  const hasActions = Boolean(actions?.length);

  const visibleColumns = headerColumns
    .map((column) => {
      const [[key, value]] = Object.entries(column);
      const columnConfig = value as string | DraggableDataTableColumnConfig<T>;

      return {
        key: key as Extract<keyof T, string>,
        label: isColumnConfig(columnConfig) ? columnConfig.label : columnConfig,
        className: isColumnConfig(columnConfig) ? columnConfig.className : undefined,
        renderCell: isColumnConfig(columnConfig) ? columnConfig.renderCell : undefined,
        shrink: isColumnConfig(columnConfig) ? columnConfig.shrink : undefined,
      };
    })
    .filter((column) => !hiddenColumns?.includes(column.key));

  let shouldShowHandle = showHandle;
  if (hideHandleOnMobile) {
    shouldShowHandle = showHandle && !isMobile;
  }

  const effectiveColumns = visibleColumns.slice(0, !isMobile ? visibleColumns.length : 1);
  const columnOffset = shouldShowHandle ? 2 : 1;
  const actionsColumnIndex = effectiveColumns.length + (shouldShowHandle ? 1 : 0) + 1;

  const tableClassName = cx(
    shouldShowHandle && 'gv-col-1-shrink',
    ...effectiveColumns.map(
      (column, index) => column.shrink && `gv-col-${index + columnOffset}-shrink`,
    ),
    hasActions &&
      actionsColumnIndex <= MAX_GRAVITY_DATA_TABLE_COLUMNS &&
      `gv-col-${actionsColumnIndex}-shrink gv-col-${actionsColumnIndex}-text-right`,
  );

  const previewLabel =
    reorder.activeIndex !== null && rows[reorder.activeIndex]
      ? getPreviewLabel?.(rows[reorder.activeIndex], reorder.activeIndex)
      : null;

  return (
    <>
      <DataTable classes={{ table: tableClassName }}>
        <thead>
          <tr>
            {shouldShowHandle && (
              <th scope="col">
                <span className="gv-sr-only">{dragHandleAriaLabel}</span>
              </th>
            )}
            {effectiveColumns.map(({ key, label }) => (
              <th key={String(key)} scope="col">
                {label}
              </th>
            ))}
            {hasActions && (
              <th scope="col">
                <span className="gv-sr-only">Actions</span>
              </th>
            )}
          </tr>
        </thead>

        <tbody>
          {rows.map((row, index) => {
            const handleDisabled = disabled || rows.length < 2;

            return (
              <tr
                key={row.id}
                ref={reorder.bindRowRef(index)}
                data-testid={testIdPrefix ? `${testIdPrefix}-row` : undefined}
                data-id={row.id}
                className={cx(
                  reorder.insertLineBeforeIndex(index) && styles.insertLine,
                  reorder.insertLineAfterLastRow(index) && styles.insertAfterLast,
                  reorder.isDragging(index) && styles.draggingGhost,
                )}
              >
                {shouldShowHandle && (
                  <td className="gv-white-space-nowrap">
                    <button
                      type="button"
                      className={cx(
                        styles.dragHandle,
                        'gv-inline-flex gv-items-center gv-justify-center gv-p-0 gv-radius',
                      )}
                      disabled={handleDisabled}
                      aria-label={dragHandleAriaLabel}
                      data-testid={testIdPrefix ? `${testIdPrefix}-drag-handle` : undefined}
                      {...reorder.bindDragHandleProps(index, { disabled: handleDisabled })}
                    >
                      <Icon name={handleIcon} size="small" aria-hidden />
                    </button>
                  </td>
                )}

                {effectiveColumns.map(({ key, className, renderCell }) => (
                  <td key={String(key)} className={className}>
                    {renderCell ? renderCell(row, index) : (row[key] as unknown as ReactNode)}
                  </td>
                ))}

                {hasActions && (
                  <td className="gv-white-space-nowrap">
                    {actions?.map((action, actionIndex) => {
                      const shouldRender = action.shouldRenderAction?.(row, index) ?? true;

                      return (
                        <button
                          key={action.label ?? actionIndex}
                          type="button"
                          className={cx('gv-button gv-button-icon', !shouldRender && 'gv-hidden')}
                          aria-label={action.label}
                          data-testid={action.testId}
                          disabled={disabled || resolveDisabled(action.disabled, row, index)}
                          onClick={() => {
                            const confirmAction = (): void => {
                              action.onAction?.(row, index);
                            };

                            if (action.requestConfirmation) {
                              action.requestConfirmation(row, index, confirmAction);
                              return;
                            }
                            confirmAction();
                          }}
                        >
                          {action.element ?? (action.icon && <Icon name={action.icon} aria-hidden />)}
                        </button>
                      );
                    })}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </DataTable>

      {shouldShowHandle && (
        <ReorderPreview position={reorder.previewPosition} iconName={handleIcon} label={previewLabel} />
      )}
    </>
  );
}
